import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Button from '../../components/Button/Button';
import { getFlightOffer } from '../../lib/duffelFlightsApi';
import './FlightDetail.css';

const formatDt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const legDuration = (dep, arr) => {
  if (!dep || !arr) return '';
  const ms = new Date(arr) - new Date(dep);
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

const sliceTitle = (count, index) => {
  if (count <= 1) return 'Itinerary';
  if (index === 0) return 'Outbound';
  if (index === 1) return 'Return';
  return `Leg ${index + 1}`;
};

const FlightDetail = ({ onGoHome, onNavigate }) => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { formatFrom } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!offerId) {
        setError('Missing flight offer.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await getFlightOffer(offerId);
        const o = res?.data;
        if (cancelled) return;
        if (!o) {
          setError('This offer could not be loaded. Search again and pick another flight.');
          return;
        }
        setOffer(o);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load flight.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [offerId]);

  const ownerName = offer?.owner?.name || offer?.slices?.[0]?.segments?.[0]?.marketing_carrier?.name || '';

  const goBook = () => {
    navigate(`/flights/book?offer=${encodeURIComponent(offerId)}`, {
      state: { offerId },
    });
  };

  return (
    <div className="flight-detail-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="flight-detail-main">
        <div className="flight-detail-container">
          <nav className="flight-detail-breadcrumb" aria-label="Breadcrumb">
            <Link to="/flights">← Back to flight search</Link>
          </nav>

          {loading ? (
            <p className="flight-detail-status">Loading flight details…</p>
          ) : null}

          {!loading && error ? (
            <div className="flight-detail-error">{error}</div>
          ) : null}

          {!loading && offer && !error ? (
            <>
              <header className="flight-detail-header">
                <h1 className="flight-detail-title">Flight details</h1>
                {ownerName ? <p className="flight-detail-airline">{ownerName}</p> : null}
                <p className="flight-detail-id">
                  Offer <span className="flight-detail-id__code">{offer.id}</span>
                </p>
              </header>

              <section className="flight-detail-price-card">
                <div>
                  <span className="flight-detail-price-label">Total</span>
                  <div className="flight-detail-price-row">
                    <span className="flight-detail-amount flight-detail-amount--converted">
                      {formatFrom(offer.total_amount, offer.total_currency)}
                    </span>
                  </div>
                  <span className="flight-detail-price-note">Final price confirmed when you continue to book</span>
                </div>
                <Button type="button" variant="primary" size="lg" onClick={goBook}>
                  Continue to book
                </Button>
              </section>

              <div className="flight-detail-legs">
                {(offer.slices || []).map((slice, sliceIdx) => (
                  <article key={slice.id || `slice-${sliceIdx}`} className="flight-detail-slice">
                    <h2 className="flight-detail-slice-title">{sliceTitle(offer.slices.length, sliceIdx)}</h2>
                    {(slice.segments || []).map((seg, segIdx) => {
                      const origin = seg.origin;
                      const dest = seg.destination;
                      const mkt = seg.marketing_carrier || seg.operating_carrier;
                      const fn =
                        seg.marketing_carrier_flight_number ||
                        seg.operating_carrier_flight_number ||
                        seg.flight_number ||
                        '';
                      const flightNum =
                        mkt?.iata_code && fn
                          ? `${mkt.iata_code} ${fn}`.trim()
                          : [mkt?.iata_code, fn].filter(Boolean).join(' ') || fn;
                      return (
                        <div key={seg.id || `seg-${segIdx}`} className="flight-detail-segment">
                          <div className="flight-detail-segment-head">
                            {flightNum ? (
                              <span className="flight-detail-flight-no">{flightNum}</span>
                            ) : null}
                            {mkt?.name ? (
                              <span className="flight-detail-carrier-name">{mkt.name}</span>
                            ) : null}
                          </div>
                          <div className="flight-detail-segment-grid">
                            <div className="flight-detail-airport">
                              <span className="flight-detail-airport__time">{formatTime(seg.departing_at)}</span>
                              <span className="flight-detail-airport__code">{origin?.iata_code || '—'}</span>
                              <span className="flight-detail-airport__name">
                                {origin?.name || origin?.city?.name || ''}
                              </span>
                            </div>
                            <div className="flight-detail-segment-mid">
                              <span className="flight-detail-segment-duration">
                                {legDuration(seg.departing_at, seg.arriving_at)}
                              </span>
                              <span className="flight-detail-segment-line" aria-hidden />
                            </div>
                            <div className="flight-detail-airport flight-detail-airport--right">
                              <span className="flight-detail-airport__time">{formatTime(seg.arriving_at)}</span>
                              <span className="flight-detail-airport__code">{dest?.iata_code || '—'}</span>
                              <span className="flight-detail-airport__name">
                                {dest?.name || dest?.city?.name || ''}
                              </span>
                            </div>
                          </div>
                          <div className="flight-detail-segment-meta">
                            <span>{formatDt(seg.departing_at)}</span>
                            <span aria-hidden> · </span>
                            <span>Arrives {formatDt(seg.arriving_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </article>
                ))}
              </div>

              <div className="flight-detail-actions">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate('/flights')}>
                  New search
                </Button>
                <Button type="button" variant="primary" size="lg" onClick={goBook}>
                  Continue to book
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FlightDetail;
